/**
 * AI Block Parsers for Sous-Chef chat
 * Extracts structured data blocks from AI responses
 */

export function parseRecipeBlock(text) {
  const jsonMatch = text.match(/```json\s*(\{[\s\S]*?"type"\s*:\s*"recipe"[\s\S]*?\})\s*```/);
  if (!jsonMatch) return null;
  try {
    const parsed = JSON.parse(jsonMatch[1]);
    if (parsed.type === "recipe" && parsed.data) return parsed.data;
  } catch { /* */ }
  return null;
}

export function removeJsonBlock(text) {
  return text.replace(/```json\s*\{[\s\S]*?"type"\s*:\s*"recipe"[\s\S]*?\}\s*```/g, "").trim();
}

export function parseActionBlock(text) {
  const match = text.match(/```action\s*([\s\S]*?)\s*```/);
  if (!match) return null;
  try { return JSON.parse(match[1]); } catch { return null; }
}

export function removeActionBlock(text) {
  return text.replace(/```action\s*[\s\S]*?\s*```/g, "").trim();
}

export function parseChecklistBlock(text) {
  const match = text.match(/```checklist\s*([\s\S]*?)\s*```/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1]);
    if (parsed.name && parsed.items) return parsed;
  } catch { /* */ }
  return null;
}

export function removeChecklistBlock(text) {
  return text.replace(/```checklist\s*[\s\S]*?\s*```/g, "").trim();
}

export function parsePriceUpdateBlock(text) {
  const match = text.match(/```price_update\s*([\s\S]*?)\s*```/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1]);
    if (parsed.ingredients) return parsed;
  } catch { /* */ }
  return null;
}

export function removePriceUpdateBlock(text) {
  return text.replace(/```price_update\s*[\s\S]*?\s*```/g, "").trim();
}

export function parseNewIngredientBlock(text) {
  const match = text.match(/```new_ingredient\s*([\s\S]*?)\s*```/);
  if (!match) return null;
  try { return JSON.parse(match[1]); } catch { return null; }
}

export function parseAllNewIngredientBlocks(text) {
  const regex = /```new_ingredient\s*([\s\S]*?)\s*```/g;
  const results = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    try {
      const parsed = JSON.parse(match[1]);
      if (parsed && parsed.name) results.push(parsed);
    } catch { /* skip invalid */ }
  }
  return results.length > 0 ? results : null;
}

export function removeNewIngredientBlock(text) {
  return text.replace(/```new_ingredient\s*[\s\S]*?\s*```/g, "").trim();
}

export function parseIngredientUpdateBlock(text) {
  const match = text.match(/```ingredient_update\s*([\s\S]*?)\s*```/);
  if (!match) return null;
  try { const p = JSON.parse(match[1]); return p.updates ? p : null; } catch { return null; }
}

export function parseIngredientMergeBlock(text) {
  const match = text.match(/```ingredient_merge\s*([\s\S]*?)\s*```/);
  if (!match) return null;
  try { const p = JSON.parse(match[1]); return p.merges ? p : null; } catch { return null; }
}

export function parseIngredientDeleteBlock(text) {
  const match = text.match(/```ingredient_delete\s*([\s\S]*?)\s*```/);
  if (!match) return null;
  try { const p = JSON.parse(match[1]); return p.names ? p : null; } catch { return null; }
}

export function parseExportBlock(text) {
  const match = text.match(/```export_request\s*([\s\S]*?)\s*```/);
  if (!match) return null;
  try { return JSON.parse(match[1]); } catch { return null; }
}

export function removeAllAIBlocks(text) {
  return text
    .replace(/```new_ingredient\s*[\s\S]*?\s*```/g, "")
    .replace(/```ingredient_update\s*[\s\S]*?\s*```/g, "")
    .replace(/```ingredient_merge\s*[\s\S]*?\s*```/g, "")
    .replace(/```ingredient_delete\s*[\s\S]*?\s*```/g, "")
    .replace(/```export_request\s*[\s\S]*?\s*```/g, "")
    .trim();
}

export function formatMessage(text) {
  const cleaned = removeAllAIBlocks(removePriceUpdateBlock(removeChecklistBlock(removeActionBlock(removeJsonBlock(text)))));
  let formatted = cleaned
    .replace(/### (.*?)(\n|$)/g, '<h4 class="text-xs font-bold text-zinc-900 mt-2 mb-0.5 uppercase tracking-wide">$1</h4>')
    .replace(/## (.*?)(\n|$)/g, '<h3 class="text-sm font-bold text-zinc-900 mt-2 mb-1">$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-zinc-900">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="bg-zinc-200/60 px-1 py-0.5 rounded text-[10px] font-mono">$1</code>')
    .replace(/^- (.*)/gm, '<li class="ml-3 text-xs leading-relaxed">$1</li>')
    .replace(/^\d+\. (.*)/gm, '<li class="ml-3 text-xs leading-relaxed list-decimal">$1</li>')
    .replace(/\n/g, '<br/>');
  formatted = formatted.replace(/((?:<li[^>]*>.*?<\/li><br\/>?)+)/g, '<ul class="space-y-0.5 my-1">$1</ul>');
  return formatted;
}

export function getSuggestions(path) {
  const base = [
    "Elabore uma receita de pão francês artesanal",
    "Analise meu CMV e sugira otimizações",
  ];
  const map = {
    "/": ["O que posso otimizar no meu negócio com base nos dados?", "Quais ingredientes devo usar antes que vençam?"],
    "/ingredients": ["Quais ingredientes cadastrados têm melhor custo-benefício?", "Sugira ingredientes base para uma cozinha profissional"],
    "/stock": ["Analise meu estoque e sugira receitas para aproveitamento", "Quais itens estão com estoque crítico?"],
    "/technical-sheets": ["Elabore uma receita com técnica de alta gastronomia", "Qual ficha tem maior potencial de otimização de custo?"],
    "/pricing": ["Analise minhas margens e sugira ajustes", "Compare markup vs margem para meus produtos"],
    "/events": ["Sugira um menu degustação para evento de 50 pessoas", "Como calcular custo por convidado com eficiência?"],
    "/shopping-list": ["Gere uma lista otimizada agrupada por fornecedor", "Quais insumos devo manter em estoque de segurança?"],
    "/dre": ["Analise meu DRE e identifique oportunidades de melhoria", "Como reduzir meu CMV mantendo qualidade?"],
    "/menu-builder": ["Sugira harmonizações baseadas em ciência dos sabores", "Monte um cardápio sazonal equilibrado"],
    "/attack-plan": ["Gere um cronograma de produção otimizado", "Como organizar mise en place para alta demanda?"],
    "/waste-control": ["Analise meus padrões de desperdício e sugira soluções", "Como implementar aproveitamento integral?"],
    "/suppliers": ["Como negociar melhores preços com fornecedores?", "Sugira diversificação de fornecedores por categoria"],
    "/menu-engineering": ["Interprete minha matriz de engenharia de cardápio", "Como converter produtos Quebra-cabeça em Estrela?"],
  };
  return map[path] || base;
}

export const findMatchingIngredient = (name, existingList) => {
  const stop = new Set(['de', 'do', 'da', 'dos', 'das', 'em', 'com', 'para', 'e']);
  const normalize = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/s\/\s*/g, 'sem ').replace(/c\/\s*/g, 'com ').replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(t => t && !stop.has(t)).join(' ');
  const n = normalize(name);
  if (!n) return null;
  const exact = existingList.find(e => normalize(e.name) === n);
  if (exact) return exact;
  const tokenSet = existingList.find(e => {
    const en = normalize(e.name);
    return en && new Set(en.split(' ')).size === new Set(n.split(' ')).size && en.split(' ').every(t => n.split(' ').includes(t));
  });
  if (tokenSet) return tokenSet;
  const partial = existingList.find(e => { const en = normalize(e.name); return en && (en.includes(n) || n.includes(en)); });
  return partial || null;
};
